import { Helmet } from "react-helmet-async";
import { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { supportLanguageList } from "constants/base";
import { isBrowserEnv, TailwindMap } from "@app/utils/constants";
import { isCnSite } from "constants/base";
import { useSSRTheme } from "@app/utils/common";
import { useRecoilValue } from "recoil";
import { ThemeSelector } from "store/data/Base";
import useFastestImageDomain from "hooks/useFastestImageDomain";
import RequestInfo from "@app/server/requestInfo";
import { DEFAULT_LANG } from "constants/vars";
import { ThemeEnum } from "@app/utils/constants";
import {
  filterDangerousHrefAndSrc,
  getDomainByLocaleAndHost,
} from "util/SaasUtils";
const particularHrefLangs = {
  "zh-CN": "zh-Hans",
  "zh-TW": "zh-Hant",
  ph: "fil",
};

const metaTags = isCnSite
  ? []
  : [
      {
        name: "google-site-verification",
        content: "kPBGKEUr5X1zRNHOxp4-SWcG8sPUBy_VP14eX2zDbvY",
      },
      {
        name: "google-site-verification",
        content: "WiR4ndcRcoZlPHifEavU_L54jq_bnP-l5i6KcMXiq1E",
      },
      {
        name: "naver-site-verification",
        content: "6db46ab91fca886da8110d45e1e0f7d76e46e720",
      },
      {
        name: "facebook-domain-verification",
        content: "z1pr98mb69v0dr6jkt7ruh11q7n4jx",
      },
    ];

const CommonSEO = () => {
  const host = RequestInfo.header?.["custom-host"];
  const { pathname } = useLocation();
  const { locales } = useParams();
  const unLangPathname = useMemo(() => {
    const langReg = new RegExp(
      `^/(${supportLanguageList.map((lang) => lang.locale).join("|")})/`
    );
    const selfPath = pathname.replace(langReg, "");
    return /^\//.test(selfPath) ? selfPath : `/${selfPath}`;
  }, [pathname]);
  const theme = useSSRTheme();
  const themeState = useRecoilValue(ThemeSelector);

  const isValidTheme = (newTheme: any): newTheme is ThemeEnum => {
    return Object.values(ThemeEnum).includes(newTheme);
  };

  const className = useMemo(() => {
    return isBrowserEnv ? TailwindMap[themeState] : TailwindMap[theme];
  }, [theme, themeState]);
  const bodyClassName = useMemo(() => {
    const newTheme = isBrowserEnv ? themeState : theme;
    if (isValidTheme(newTheme)) {
      return newTheme;
    }
    return ThemeEnum.black;
  }, [theme, themeState]);

  const href = useMemo(() => {
    return getDomainByLocaleAndHost(locales || DEFAULT_LANG, host) + pathname;
  }, [host, locales, pathname]);

  const noLangHref = useMemo(() => {
    return getDomainByLocaleAndHost(DEFAULT_LANG, host) + unLangPathname;
  }, [host, unLangPathname]);

  const helmetProps = useMemo(() => {
    const dir = locales === "ar" ? "rtl" : "ltr";
    const lang = (locales === "asia" ? "en" : locales) ?? "en";

    // 客户端helmet有bug，暂时不用
    if (isBrowserEnv) {
      document.documentElement.setAttribute("dir", dir);
      document.documentElement.setAttribute("lang", lang);
      document.documentElement.setAttribute("class", className);
      return {};
    }
    return {
      htmlAttributes: {
        lang,
        dir,
        class: className,
      },
    };
  }, [className, locales]);

  const { src: imgSrc } = useFastestImageDomain(
    "https://img.cointrimg.com/image/third/share_graph_laliga.webp"
  );

  return (
    <>
      <Helmet
        {...helmetProps}
        bodyAttributes={{
          class: bodyClassName,
        }}
      >
        <>
          <meta charSet="utf-8" />
          <meta
            name="viewport"
            content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1,user-scalable=no"
          />
          <meta name="theme-color" content="#000000" />

          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Cointr" />
          <meta property="og:image" content={imgSrc} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@cointrglobal" />
          <meta name="twitter:image" content={imgSrc} />

          <link rel="canonical" href={filterDangerousHrefAndSrc(href)} />
          <meta property="og:url" content={href} />
          <meta property="twitter:url" content={href} />
          {metaTags.map((meta, index) => (
            <meta key={index} name={meta.name} content={meta.content} />
          ))}

          {supportLanguageList
            .filter((item) => item.locale !== "asia")
            .map((lan, index) => {
              return (
                <link
                  key={index}
                  rel="alternate"
                  hrefLang={
                    particularHrefLangs[lan.locale]
                      ? particularHrefLangs[lan.locale]
                      : lan.locale
                  }
                  href={filterDangerousHrefAndSrc(
                    `${getDomainByLocaleAndHost(lan.locale, host)}${
                      lan.locale === "en" ? "" : `/${lan.locale}`
                    }${unLangPathname}`
                  )}
                />
              );
            })}
          <link
            rel="alternate"
            hrefLang="en-NG"
            href={filterDangerousHrefAndSrc(
              `https://www.cointr.ng${unLangPathname}`
            )}
          />
          {["en-IN", "en-MY"].map((lan) => {
            return (
              <link
                key={lan}
                rel="alternate"
                hrefLang={lan}
                href={filterDangerousHrefAndSrc(
                  `${getDomainByLocaleAndHost(lan, host)}${unLangPathname}`
                )}
              />
            );
          })}
          <link
            rel="alternate"
            hrefLang="x-default"
            href={filterDangerousHrefAndSrc(noLangHref || "")}
          />
        </>
      </Helmet>
    </>
  );
};

export default CommonSEO;
